"use strict";
/**
 * THE BADGES A DEVICE UNIT CAN CARRY.
 *
 * Shared so the UI and hardware-service cannot disagree about what a badge id
 * means. A badge is a durable statement about a physical device — "this one
 * arrived scratched", "this one passed QA" — and every apply and remove is
 * written to the unit's audit trail with a timestamp, so the device's history
 * answers "did it come back good?" without anyone having to remember.
 *
 * `severity` is the colour role, not decoration:
 *   good  — nothing to act on; the happy path, recorded so silence is not ambiguous
 *   watch — usable, but somebody should know
 *   fault — not fit to ship as-is
 *
 * IDS ARE PERMANENT. They are stored on units and in audit rows that are never
 * rewritten, so renaming a `label` is safe and changing an `id` is not. Retiring
 * a badge means dropping it from this list; units that already carry it keep it,
 * which is why the stored field is a tolerant `string[]` rather than an enum.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.unitBadgeLabel = exports.unitBadge = exports.UNIT_BADGES = void 0;
exports.worstBadgeSeverity = worstBadgeSeverity;
exports.UNIT_BADGES = [
    // good — the outcome you hope to record, and the one most worth having
    // explicitly rather than inferring from an absence of bad news.
    { id: 'received_good', label: 'Received good', severity: 'good' },
    { id: 'quality_checked', label: 'Quality checked', severity: 'good' },
    { id: 'refurbished', label: 'Refurbished', severity: 'good' },
    // watch — ships, but the next person handling it should know.
    { id: 'scratched', label: 'Scratched', severity: 'watch' },
    { id: 'box_damaged', label: 'Box damaged', severity: 'watch' },
    { id: 'missing_accessory', label: 'Missing accessory', severity: 'watch' },
    { id: 'hold_qa', label: 'Hold for QA', severity: 'watch' },
    // fault — not fit to send to a merchant as it stands.
    { id: 'dented', label: 'Dented / cracked', severity: 'fault' },
    { id: 'screen_fault', label: 'Screen fault', severity: 'fault' },
    { id: 'return_vendor', label: 'Return to vendor', severity: 'fault' },
];
const BY_ID = new Map(exports.UNIT_BADGES.map((b) => [b.id, b]));
/** The badge for an id, or undefined for one that has since been retired. */
const unitBadge = (id) => BY_ID.get(id);
exports.unitBadge = unitBadge;
/**
 * A human label for a badge id.
 *
 * Falls back to the raw id rather than to an empty string: a unit carrying a
 * retired badge should still show SOMETHING in its history, because that row
 * is a record of what someone actually observed.
 */
const unitBadgeLabel = (id) => BY_ID.get(id)?.label ?? id;
exports.unitBadgeLabel = unitBadgeLabel;
/** The worst severity present, for a single at-a-glance colour on a row. */
function worstBadgeSeverity(ids) {
    let worst;
    for (const id of ids) {
        const s = BY_ID.get(id)?.severity;
        if (s === 'fault')
            return 'fault';
        if (s === 'watch')
            worst = 'watch';
        else if (s === 'good' && !worst)
            worst = 'good';
    }
    return worst;
}
//# sourceMappingURL=unitBadges.js.map